import { Weekdays } from '../interfaces/date';
import { convertToEnglishDigits } from './string';
import * as moment from 'jalali-moment';

export const TEHRAN_TIME_ZONE = 'Asia/Tehran';

export type TehranDateRange = {
  start: Date;
  end: Date;
};

type GregorianDateParts = {
  year: number;
  month: number;
  day: number;
};

const tehranDateFormatter = new Intl.DateTimeFormat('en-US-u-nu-latn', {
  timeZone: TEHRAN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const tehranDateTimeFormatter = new Intl.DateTimeFormat('en-US-u-nu-latn', {
  timeZone: TEHRAN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export function formatJalali(
  dateInput: any,
  withTime: boolean = true,
  timeZone?: string,
): string {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  const dateOpts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const timeOpts: Intl.DateTimeFormatOptions = withTime
    ? { hour: '2-digit', minute: '2-digit' }
    : {};
  const fmt = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    ...dateOpts,
    ...timeOpts,
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  });
  return fmt.format(d);
}

/**
 * Formats a date as a Jalali date key (YYYY-MM-DD). Jalali date keys received
 * from the API are kept in the same calendar instead of being parsed as old
 * Gregorian dates by the native Date constructor.
 */
export function formatJalaliDateKey(
  dateInput: string | Date | null | undefined,
  timeZone: string = TEHRAN_TIME_ZONE,
): string {
  if (!dateInput) return '';

  const rawValue = convertToEnglishDigits(dateInput).trim();
  const dateKeyMatch = rawValue.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/,
  );

  if (dateKeyMatch) {
    const year = Number(dateKeyMatch[1]);

    if (year >= 1200 && year <= 1600) {
      const normalizedDateKey = `${year}-${dateKeyMatch[2].padStart(
        2,
        '0',
      )}-${dateKeyMatch[3].padStart(2, '0')}`;
      const jalaliDate = safeJalaliMoment(normalizedDateKey.replace(/-/g, '/'));

      return jalaliDate?.clone().locale('en').format('jYYYY-jMM-jDD') ===
        normalizedDateKey
        ? normalizedDateKey
        : '';
    }
  }

  const date = dateInput instanceof Date ? dateInput : new Date(rawValue);
  if (!Number.isFinite(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat(
    'en-US-u-ca-persian-nu-latn',
    {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
  );
  const parts = getNumericDateTimeParts(formatter, date);

  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(
    parts.day,
  ).padStart(2, '0')}`;
}

/**
 * Returns a half-open range [start, end) whose day boundaries are midnight in
 * Iran. `daysAgo` shifts the whole range back by Iranian calendar days.
 */
export function getTehranDayRange(
  dayCount: number = 1,
  daysAgo: number = 0,
  now: Date = new Date(),
): TehranDateRange {
  const safeDayCount = Math.max(1, Math.floor(dayCount));
  const safeDaysAgo = Math.max(0, Math.floor(daysAgo));
  const today = getTehranDateParts(now);
  const startDate = shiftGregorianDate(
    today,
    -(safeDaysAgo + safeDayCount - 1),
  );
  const endDate = shiftGregorianDate(today, 1 - safeDaysAgo);

  return {
    start: tehranMidnightToUtc(startDate),
    end: tehranMidnightToUtc(endDate),
  };
}

export function isInTehranDateRange(
  value: string | Date | null | undefined,
  range: TehranDateRange,
): boolean {
  if (!value) return false;

  const date = value instanceof Date ? value : new Date(value);
  const timestamp = date.getTime();

  return (
    Number.isFinite(timestamp) &&
    timestamp >= range.start.getTime() &&
    timestamp < range.end.getTime()
  );
}

/**
 * Daily stat rows carry their business day in the `date` field using the Jalali
 * calendar (e.g. "1405-05-11"). Convert such a value to the UTC instant of that
 * day's midnight in Tehran so it can be filtered, sorted and formatted like any
 * other date. Full ISO timestamps and Gregorian calendar dates are passed
 * through unchanged; unparseable input yields null.
 */
export function parseTehranBusinessDay(
  value: string | Date | null | undefined,
): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  const raw = convertToEnglishDigits(String(value)).trim();
  if (!raw) return null;

  // Full ISO timestamps are already Gregorian instants.
  if (raw.includes('T')) {
    const iso = new Date(raw);
    return Number.isFinite(iso.getTime()) ? iso : null;
  }

  const parts = raw.match(/^(\d{3,4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!parts) {
    const parsed = new Date(raw);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }

  let year = Number(parts[1]);
  let month = Number(parts[2]);
  let day = Number(parts[3]);

  // Jalali years sit far below Gregorian ones; use the calendar the year implies.
  if (year < 1700) {
    const jalali = safeJalaliMoment(`${year}/${month}/${day}`)?.locale('en');
    if (!jalali) return null;
    year = jalali.year();
    month = jalali.month() + 1;
    day = jalali.date();
  }

  return tehranMidnightToUtc({ year, month, day });
}

/**
 * Rewrites each row's business-day `date` (Jalali on the API) to a Gregorian
 * ISO instant via `parseTehranBusinessDay`, falling back to `createdAt`. Lets
 * every consumer filter, sort and format these rows with standard `Date` logic.
 */
export function normalizeBusinessDayDates<
  T extends { date?: string; createdAt?: string }
>(items: T[] | null | undefined): T[] {
  if (!items) return [];

  return items.map((item) => {
    const businessDay =
      parseTehranBusinessDay(item.date) ??
      parseTehranBusinessDay(item.createdAt);

    return businessDay ? { ...item, date: businessDay.toISOString() } : item;
  });
}

export function getTehranDateKey(
  value: string | Date | null | undefined,
): string {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '';

  const parts = getNumericDateTimeParts(tehranDateFormatter, date);
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(
    parts.day,
  ).padStart(2, '0')}`;
}

export function millisecondsUntilNextTehranMidnight(
  now: Date = new Date(),
): number {
  const { end } = getTehranDayRange(1, 0, now);
  return Math.max(1000, end.getTime() - now.getTime() + 1000);
}

function getTehranDateParts(date: Date): GregorianDateParts {
  const parts = getNumericDateTimeParts(tehranDateFormatter, date);
  return { year: parts.year, month: parts.month, day: parts.day };
}

function getNumericDateTimeParts(
  formatter: Intl.DateTimeFormat,
  date: Date,
): Record<string, number> {
  const result: Record<string, number> = {};

  formatter.formatToParts(date).forEach((part) => {
    if (part.type !== 'literal') {
      result[part.type] = Number(part.value);
    }
  });

  return result;
}

function shiftGregorianDate(
  date: GregorianDateParts,
  days: number,
): GregorianDateParts {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function tehranMidnightToUtc(date: GregorianDateParts): Date {
  const localTimestamp = Date.UTC(date.year, date.month - 1, date.day);
  let offset = getTehranUtcOffset(new Date(localTimestamp));
  let result = new Date(localTimestamp - offset);
  const correctedOffset = getTehranUtcOffset(result);

  if (correctedOffset !== offset) {
    offset = correctedOffset;
    result = new Date(localTimestamp - offset);
  }

  return result;
}

function getTehranUtcOffset(date: Date): number {
  const parts = getNumericDateTimeParts(tehranDateTimeFormatter, date);
  // Some Intl implementations represent midnight as 24:00 of the same date.
  const hour = parts.hour === 24 ? 0 : parts.hour;
  const displayedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    hour,
    parts.minute,
    parts.second,
  );
  const timestampWithoutMilliseconds =
    Math.floor(date.getTime() / 1000) * 1000;

  return displayedAsUtc - timestampWithoutMilliseconds;
}

export function calculateAgeFromBirthday(
  birthdayInput?: string | null,
): number | null {
  const birthday = parseBirthday(birthdayInput);
  if (!birthday) return null;

  const today = moment().locale('en').startOf('day');
  let age = today.year() - birthday.year();

  if (
    today.month() < birthday.month() ||
    (today.month() === birthday.month() && today.date() < birthday.date())
  ) {
    age--;
  }

  return age >= 0 ? age : null;
}

function parseBirthday(value?: string | null): any | null {
  if (!value) return null;

  const raw = convertToEnglishDigits(String(value)).trim();
  if (!raw) return null;

  const dateParts = extractDateParts(raw);
  if (!dateParts) return null;

  return parseByCalendarYear(dateParts, raw) || parseFutureEncodedBirthday(raw);
}

function extractDateParts(value: string): {
  year: number;
  month: number;
  day: number;
} | null {
  const dateMatch = value.match(/^(\d{3,4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!dateMatch) return null;

  return {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
  };
}

function parseByCalendarYear(
  dateParts: { year: number; month: number; day: number },
  rawValue: string,
): any | null {
  const currentYear = moment().year();
  const currentJalaliYear = moment().jYear();

  if (dateParts.year >= 1900 && dateParts.year <= currentYear) {
    return ensureReasonableBirthday(parseGregorianDate(dateParts, rawValue));
  }

  if (dateParts.year >= 1200 && dateParts.year <= currentJalaliYear) {
    return ensureReasonableBirthday(parseJalaliDate(dateParts));
  }

  return null;
}

function parseGregorianDate(
  dateParts: { year: number; month: number; day: number },
  rawValue: string,
): any | null {
  if (rawValue.includes('T')) {
    const isoDate = safeIsoMoment(rawValue);
    if (isoDate) return isoDate;
  }

  return safeMoment(
    `${dateParts.year}-${dateParts.month}-${dateParts.day}`,
    ['YYYY-M-D'],
    true,
  );
}

function parseJalaliDate(dateParts: {
  year: number;
  month: number;
  day: number;
}): any | null {
  return safeJalaliMoment(
    `${dateParts.year}/${dateParts.month}/${dateParts.day}`,
  );
}

function parseFutureEncodedBirthday(value: string): any | null {
  const isoDate = safeIsoMoment(value);
  if (!isoDate) return null;

  const currentYear = moment().year();
  if (isoDate.year() <= currentYear) return null;

  try {
    const encodedAsGregorian = isoDate
      .clone()
      .locale('en')
      .format('jYYYY-jMM-jDD');
    const dateParts = extractDateParts(encodedAsGregorian);

    if (!dateParts) return null;
    if (dateParts.year < 1900 || dateParts.year > currentYear) return null;

    return ensureReasonableBirthday(
      parseGregorianDate(dateParts, encodedAsGregorian),
    );
  } catch {
    return null;
  }
}

function ensureReasonableBirthday(date: any | null): any | null {
  if (!date?.isValid?.()) return null;

  const birthday = date.clone().locale('en').startOf('day');
  const today = moment().locale('en').startOf('day');
  const year = birthday.year();

  if (year < 1900 || birthday.isAfter(today, 'day')) return null;
  return birthday;
}

function safeMoment(
  value: string,
  formats: string[],
  strict: boolean,
): any | null {
  try {
    const date = moment(value, formats, strict);
    return date.isValid() ? date : null;
  } catch {
    return null;
  }
}

function safeIsoMoment(value: string): any | null {
  try {
    const date = moment(value, moment.ISO_8601, true);
    return date.isValid() ? date : null;
  } catch {
    return null;
  }
}

function safeJalaliMoment(value: string): any | null {
  try {
    const date = moment.from(value, 'fa', 'YYYY/M/D');
    return date.isValid() ? date : null;
  } catch {
    return null;
  }
}

export const weekdays: { label: string; value: Weekdays }[] = [
  { label: 'شنبه', value: Weekdays.SATURDAY },
  { label: 'یکشنبه', value: Weekdays.SUNDAY },
  { label: 'دوشنبه', value: Weekdays.MONDAY },
  { label: 'سه‌شنبه', value: Weekdays.TUESDAY },
  { label: 'چهارشنبه', value: Weekdays.WEDNSDAY },
  { label: 'پنج‌شنبه', value: Weekdays.THURSDAY },
  { label: 'جمعه', value: Weekdays.FRIDAY },
];

export function timeAgo(dateString: string) {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now.getTime() - past.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return `${seconds} ثانیه قبل`;
  if (minutes < 60) return `${minutes} دقیقه قبل`;
  if (hours < 24) return `${hours} ساعت قبل`;
  if (days < 7) return `${days} روز قبل`;
  if (weeks < 4) return `${weeks} هفته قبل`;
  if (months < 12) return `${months} ماه قبل`;
  return `${years} سال قبل`;
}
